/**
 * Botões do controle de apresentação (spec 011, pedido do Leopoldo de
 * 22/10/2026): o receptor USB "mini keyboard" (VID 1997) que ele usa no
 * consultório. Cada botão manda sempre a mesma tecla (conferido na captura de
 * 22/10) e não dá pra reprogramar, então o sistema aceita as teclas dele:
 *
 *   F5      (iniciar apresentação) → liga/desliga o ditado, como o Alt+V
 *   Ctrl+P  (caneta)               → imprime o que está pronto na fila
 *   Ctrl+E  (borracha)             → "Atlas": espera o pedido, como dizer o nome
 *
 * SEGURAR (24/10/2026, pedido do Leopoldo — botões que sobram são letra/Esc):
 *
 *   Ctrl+P segurado (caneta)   → o "Prontuário" da coluna do ditado
 *   Ctrl+E segurado (borracha) → "Mandar entrar" genérico (sem nome)
 *
 * Por isso caneta e borracha agem ao SOLTAR (toque curto) ou ao completar
 * `SEGURAR_MS` ainda apertado (`DictationHotkey`).
 *
 * B, W e Esc também saem do controle, mas são letra/tecla de uso comum: ficam
 * de fora. O navegador não sabe de que aparelho veio a tecla, então o F5 do
 * teclado também liga o ditado (na ficha) — por isso é preferência DESTE
 * computador, desligada por padrão. Independe da escuta "Jarvis/Atlas": a
 * tecla do Atlas liga o reconhecedor só pro pedido (`WakeWordWatcher`).
 *
 * TECLADO PROGRAMÁVEL (28/10/2026, SayoDevice 2x3B do Leopoldo): F13/F14/F15
 * fazem o mesmo que F5/Ctrl+P/Ctrl+E, SEMPRE (sem a preferência). Nenhum
 * teclado comum tem essas teclas e o Chrome não faz nada com elas — com o foco
 * fora da página o Ctrl+P abria a impressão da página (visto em 27/10); o F14
 * só não faz nada.
 */

import { useSyncExternalStore } from 'react';

const LS_KEY = 'dratento.voice.controle.v1';

export type BotaoControle = 'ditado' | 'imprimir' | 'atlas';

/** Quanto tempo apertado vira "segurar" (o Windows começa a repetir a tecla em ~0,5 s). */
export const SEGURAR_MS = 700;

/** Teclas do teclado programável: valem sempre. */
const TECLAS_PROGRAMAVEIS: Record<string, BotaoControle> = { F13: 'ditado', F14: 'imprimir', F15: 'atlas' };

/**
 * Qual botão do controle é esta tecla (ou `null`). F5/Ctrl+P/Ctrl+E só com o
 * controle `ligado` neste computador. AltGr chega como Ctrl+Alt: fica de fora.
 */
export function botaoDoControle(
  e: Pick<KeyboardEvent, 'code' | 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey'>,
  ligado: boolean,
): BotaoControle | null {
  if (e.altKey || e.metaKey || e.shiftKey) return null;
  const programavel = e.ctrlKey ? undefined : TECLAS_PROGRAMAVEIS[e.code];
  if (programavel) return programavel;
  if (!ligado) return null;
  if (!e.ctrlKey) return e.code === 'F5' ? 'ditado' : null;
  if (e.code === 'KeyP') return 'imprimir';
  if (e.code === 'KeyE') return 'atlas';
  return null;
}

function ler(): boolean {
  try {
    return localStorage.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
}

let ligado = typeof window !== 'undefined' && ler();
const ouvintes = new Set<() => void>();

if (typeof window !== 'undefined') {
  // Outra aba mudou a preferência.
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_KEY) return;
    ligado = ler();
    ouvintes.forEach((f) => f());
  });
}

export function getControleLigado(): boolean {
  return ligado;
}

export function setControleLigado(next: boolean): void {
  ligado = next;
  try {
    if (next) localStorage.setItem(LS_KEY, '1');
    else localStorage.removeItem(LS_KEY);
  } catch {
    /* storage bloqueado: vale só nesta sessão */
  }
  ouvintes.forEach((f) => f());
}

function assinar(f: () => void): () => void {
  ouvintes.add(f);
  return () => ouvintes.delete(f);
}

export function useControleLigado(): boolean {
  return useSyncExternalStore(assinar, () => ligado, () => false);
}
